import { Form, Input, NoticeBar, Toast } from 'antd-mobile';
import outStyles from '../../index.module.scss';
import styles from './index.module.scss';
import { CalendarOutline, ExclamationCircleOutline } from 'antd-mobile-icons';
import TimePickerView from '../TimePickerView';
import { useCallback, useState } from 'react';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { formatTimeRangesWithTomorrow, parseTimeRange } from '../../util';

type AddTimeProps = {
  date?: string;
  disabledTimes?: Array<any>;
  selectedTimeRange?: Array<string> | null;
  onCancel?: () => void;
  onConfirm?: (startTime: Array<string | null>, endTime: Array<string | null>) => void;
};

const AddTime = ({ date, disabledTimes, selectedTimeRange, onConfirm, onCancel }: AddTimeProps) => {
  const [startTime, setStartTime] = useState<Array<string | null>>();
  const [endTime, setEndTime] = useState<Array<string | null>>();
  const [activeField, setActiveField] = useState<'startTime' | 'endTime'>('startTime');

  const [form] = Form.useForm();
  // TimePickerView 的 value
  const value = activeField === 'startTime' ? startTime ?? [, dayjs().format('HH'), ':30'] : endTime;

  // 选择时间后回填到对应字段
  const handleTimeChange = useCallback(
    (val?: Array<string | null>) => {
      if (activeField === 'startTime') {
        setStartTime(val);
        form.setFieldsValue({ startTime: val?.join('') });
      } else {
        setEndTime(val);
        form.setFieldsValue({ endTime: val?.join('') });
      }
    },
    [activeField]
  );

  // 点击 startTime 输入框
  const handleStartTimeClick = () => {
    setActiveField('startTime');
  };

  // 点击 endTime 输入框
  const handleEndTimeClick = () => {
    setActiveField('endTime');
    setEndTime(endTime);
  };

  // 确定按钮
  const handleConfirm = () => {
    const disableList = [...(disabledTimes ?? []), ...(selectedTimeRange ?? [])];
    let sTime = startTime!;
    let eTime = endTime!;
    // 格式化用户选择的开始/结束时间
    const [{ startDateTime, endDateTime }] = formatTimeRangesWithTomorrow(date!, [
      { startDateTime: sTime.join(''), endDateTime: eTime.join('') },
    ]);
    let selectedStart = dayjs(startDateTime);
    let selectedEnd = dayjs(endDateTime);
    // 如果开始时间晚于结束时间，则对换
    if (selectedStart.isAfter(selectedEnd)) {
      [sTime, eTime] = [eTime, sTime];
      [selectedStart, selectedEnd] = [selectedEnd, selectedStart];
    }
    if (disableList.length === 0) {
      onCancel?.();
      return onConfirm!(sTime, eTime);
    }
    // 是否与禁用时间段冲突
    const hasConflict = disableList
      .map(t => parseTimeRange(date!, t))
      .some(item => {
        const disabledStart = dayjs(item.startDateTime);
        const disabledEnd = dayjs(item.endDateTime);
        // 时间区间重叠判断： A.start < B.end && A.end > B.start
        return selectedStart.isBefore(disabledEnd) && selectedEnd.isAfter(disabledStart);
      });
    if (hasConflict) {
      Toast.show('新增时段冲突');
      return;
    }
    onCancel?.();
    onConfirm!(sTime, eTime);
  };

  return (
    <div className={styles.container}>
      <div className={outStyles.popupTitleContainer}>
        <div className={outStyles.linkBtn} onClick={onCancel}>
          取消
        </div>
        <div className={outStyles.popupTitle}>新增时段</div>
        <div
          className={outStyles.linkBtn}
          style={{ cursor: 'pointer', color: startTime && endTime ? undefined : '#ccc' }}
          onClick={startTime && endTime ? handleConfirm : undefined}
        >
          确定
        </div>
      </div>
      <div className={`${outStyles.popupContent} ${styles.popupContent}`}>
        {(!isEmpty(disabledTimes) || !isEmpty(selectedTimeRange)) && (
          <NoticeBar
            color="alert"
            wrap={true}
            icon={<ExclamationCircleOutline />}
            content={
              <>
                不可选时段：
                {Array.from(new Set([...(disabledTimes ?? []), ...(selectedTimeRange ?? [])])).join('、')}
              </>
            }
          />
        )}
        <Form className={styles.timeRange} form={form}>
          <Form.Item
            name="startTime"
            extra={<CalendarOutline />}
            onClick={handleStartTimeClick}
            className={activeField === 'startTime' ? styles.selected : ''}
          >
            <Input placeholder="未选择" readOnly onClick={handleStartTimeClick} style={{ cursor: 'pointer' }} />
          </Form.Item>
          <span style={{ color: '#CCCCCC' }}>一</span>
          <Form.Item
            name="endTime"
            extra={<CalendarOutline />}
            onClick={handleEndTimeClick}
            arrow={null}
            className={activeField === 'endTime' ? styles.selected : ''}
          >
            <Input placeholder="未选择" readOnly style={{ cursor: 'pointer' }} />
          </Form.Item>
        </Form>
        <TimePickerView value={value} onChange={handleTimeChange} />
      </div>
    </div>
  );
};

export default AddTime;
